/**
 * Productcatalogus — overgenomen uit de huidige webshop (beautybewust.nl/winkel),
 * september 2026. Prijzen, namen, beelden en Sarah's eigen productteksten komen
 * één-op-één uit WooCommerce; `id` is de WooCommerce-slug, zodat oude links en
 * de latere koppeling (docs/server/woocommerce-mapping.ts) blijven kloppen.
 *
 * Nog aan te vullen door Sarah: inhoud (ml), hoelang een product meegaat,
 * en controle van het gebruiksadvies. Velden die leeg zijn, verbergt de site.
 *
 * Niet overgenomen (passen niet in een huidroutine of zijn niet te bestellen):
 * Raaw Alchemy parfums, Kansa Wand, Gua Sha steen, emerginC sample set,
 * Medik8 Advanced Night Restore (staat op € 0).
 */

import type { Brand, Product } from '../core/types/catalog'

export const brands: Brand[] = [
  {
    name: 'emerginC',
    description:
      'Cosmeceutical: ingrediënten die bijna medicinaal werken. Eigen stay-C formule, astaxanthine en de koudgeperste Rawceuticals-lijn. Een boom geplant per verkocht product.',
  },
  {
    name: 'Medik8',
    description:
      'Britse, wetenschappelijk onderbouwde lijn rond vitamine A en C. Vegan en dierproefvrij, en opbouwbaar in sterkte — zodat je huid rustig kan wennen.',
  },
]

const UP = 'https://beautybewust.nl/wp-content/uploads/'

/** Standaard gebruiksadvies per soort product — Sarah kan per product overschrijven. */
const use = {
  cleanser:
    'Ochtend en avond op een vochtige huid masseren en afspoelen met lauw water. ’s Avonds twee keer reinigen als je make-up of SPF droeg.',
  toner: 'Na het reinigen, ochtend en avond. In je handen of op een wattenschijfje en zacht indrukken.',
  serum: 'Na je toner een paar druppels op een gereinigde huid, vóór je crème. Laat even intrekken.',
  vitC: '’s Ochtends na je toner, vóór je crème. Altijd afsluiten met SPF — dan werkt vitamine C het best tegen pigment.',
  retinal:
    '’s Avonds, begin twee à drie avonden per week en bouw rustig op. Overdag altijd SPF. Niet gebruiken tijdens zwangerschap of borstvoeding.',
  cream: 'Ochtend en/of avond als laatste verzorgende stap, na je serum.',
  spf: 'Laatste stap van je ochtendroutine, ruim aanbrengen. Bij veel zon of zweten bijwerken.',
  mask: 'Eén tot twee keer per week op een gereinigde huid. Daarna je gewone routine.',
  eye: 'Ochtend en avond een kleine hoeveelheid rond het oogbot deppen, niet wrijven.',
}

export const products: Product[] = [
  // ——— Medik8 ———
  {
    id: 'medik8-retinoate-bestseller',
    wooId: 10719,
    name: 'r-Retinoate Day & Night',
    brand: 'Medik8',
    category: 'Serum',
    step: 'Behandelen',
    price: 189,
    image: `${UP}2026/08/20230922_-_PDP_Asset_1_-_r-Retinoate_Day_Night_-_Packshot_-_DE.avif`,
    skinTypes: ['Rijpere huid', 'Pigmentatie'],
    note: 'Vitamine A voor dag én nacht: acht keer sterker dan retinol, toch zacht.',
    why: 'De Retinoate van Medik8 is een absolute bestseller en mijn persoonlijke favoriet. Een vitamine A die zowel overdag als ’s nachts gebruikt kan worden voor een snellere, zichtbare verheldering van de huid, terwijl het product zacht is en heerlijk ruikt. Acht keer sterker dan retinol en verliest niet aan kracht door zonlicht.',
    howTo:
      'Ochtend en/of avond een pompje op een gereinigde huid, na je toner. Overdag altijd afsluiten met SPF. Niet tijdens zwangerschap of borstvoeding.',
    keyIngredients: ['Retinyl retinoate (vitamine A)'],
    rating: 0,
    reviews: 0,
    refill: true,
    pairs: ['medik8-daily-radiance-vitamine-c', 'medik8-daily-refreshing-toner'],
    bestseller: true,
    salonFavourite: true,
    // Voorbeeld van de galerij (docs/BEELDEN.md, P2–P4); tot de fotoshoot sfeerbeelden.
    media: [
      {
        image: 'https://cdn.magicpatterns.com/patterns/generated-images/410a9665-58eb-404a-a2c5-921022901659.jpg',
        alt: 'Een druppel serum op de binnenkant van een pols',
        focus: { x: 50, y: 45 },
      },
      {
        image: 'https://cdn.magicpatterns.com/patterns/generated-images/a8b2ca8a-8aa3-408b-a3f7-9efba74737d3.jpg',
        alt: 'Een vrouw drukt serum met haar vingertoppen in haar wang',
        focus: { x: 60, y: 40 },
      },
      {
        image: 'https://cdn.magicpatterns.com/patterns/generated-images/755e8cbb-bbf1-4409-8764-a5815d2a03df.jpg',
        alt: '',
      },
    ],
  },
  {
    id: 'medik8-daily-radiance-vitamine-c',
    wooId: 10606,
    name: 'Daily Radiance Vitamin C SPF 30',
    brand: 'Medik8',
    category: 'SPF',
    step: 'Beschermen',
    price: 80,
    image: `${UP}2026/05/20230922_-_PDP_Asset_1_-_Daily_Radiance_Vitamin_C_-_Packshot_-_DE-1.avif`,
    skinTypes: ['Pigmentatie', 'Rijpere huid', 'Droge huid', 'Hormonale huid'],
    note: 'Dagcrème met 7% vitamine C én SPF 30 in één stap.',
    why: 'Daily Radiance Vitamin C is de dagcrème die de huid beschermt, hydrateert en laat stralen. Met 7% gestabiliseerde vitamine C biedt hij krachtige antioxidatieve bescherming tegen vrije radicalen, stimuleert hij de collageenaanmaak en helpt hij pigmentvlekken verminderen voor een egalere teint. Vitamine E versterkt de werking van vitamine C, hyaluronzuur hydrateert intensief. Dankzij breedspectrum SPF 30 is de huid dagelijks beschermd tegen uv-straling en vroegtijdige huidveroudering. Een ideale afsluitende stap in de ochtendroutine. Mijn nieuwe persoonlijke favoriet!',
    howTo: use.spf,
    keyIngredients: ['7% vitamine C', 'Vitamine E', 'Hyaluronzuur', 'SPF 30'],
    rating: 0,
    reviews: 0,
    refill: true,
    pairs: ['medik8-c-tetra-serum', 'medik8-retinoate-bestseller'],
    salonFavourite: true,
  },
  {
    id: 'medik8-chrystal-retinal-1',
    wooId: 10605,
    name: 'Crystal Retinal 1',
    brand: 'Medik8',
    category: 'Serum',
    step: 'Behandelen',
    price: 56,
    image: `${UP}2026/05/Copyof20230922-PDPAsset1-CrystalRetinal1-Packshot-ROW_f6c0f90b-93b9-492c-a8af-ded1ecb370aa-1.avif`,
    skinTypes: ['Rijpere huid', 'Pigmentatie', 'Gevoelige huid', 'Hormonale huid'],
    note: 'De zachte start met vitamine A, ook voor een gevoelige huid.',
    why: 'Crystal Retinal 1 is het paradepaardje van Medik8. Een intensief nachtserum dat de huid zichtbaar vernieuwt terwijl je slaapt: de geavanceerde vorm van vitamine A stimuleert de celvernieuwing, verfijnt fijne lijntjes en ondersteunt een egalere, stralende teint. Dankzij het time-release-systeem komt de retinal geleidelijk vrij. Dit is het startniveau, ideaal voor de gevoelige huid. Het serum bestaat in meerdere sterktes, zodat je veilig kunt opbouwen.',
    howTo: use.retinal,
    keyIngredients: ['Retinal (time-release)', 'Hyaluronzuur'],
    rating: 0,
    reviews: 0,
    refill: true,
    pairs: ['medik8-daily-radiance-vitamine-c', 'medik8-hydr8-b5'],
    salonFavourite: true,
  },
  {
    id: 'medik8-chrystal-retinal-6',
    wooId: 10610,
    name: 'Crystal Retinal 6',
    brand: 'Medik8',
    category: 'Serum',
    step: 'Behandelen',
    price: 85,
    image: `${UP}2026/05/Copyof20230922-PDPAsset1-CrystalRetinal6-Packshot-ROW.avif`,
    skinTypes: ['Rijpere huid', 'Pigmentatie', 'Hormonale huid'],
    note: 'Het volgende niveau vitamine A, voor een huid die al gewend is.',
    why: 'Crystal Retinal 6 is een intensief nachtserum dat de huid vernieuwt terwijl je slaapt. De geavanceerde vorm van vitamine A stimuleert de celvernieuwing, verfijnt fijne lijntjes en ondersteunt een egalere, stralende teint. Dankzij het time-release-systeem wordt de retinal geleidelijk afgegeven, zodat de huid stap voor stap went aan de krachtige werking. Aangevuld met hyaluronzuur om de huid soepel te houden.',
    howTo: `${use.retinal} Begin pas met deze sterkte als je Crystal Retinal 1 of 3 goed verdraagt.`,
    keyIngredients: ['Retinal (time-release)', 'Hyaluronzuur'],
    rating: 0,
    reviews: 0,
    refill: true,
    pairs: ['medik8-advanced-pro-collagen-peptide-cream', 'medik8-daily-radiance-vitamine-c'],
  },
  {
    id: 'medik8-c-tetra-serum',
    wooId: 10602,
    name: 'C-Tetra',
    brand: 'Medik8',
    category: 'Serum',
    step: 'Behandelen',
    price: 56,
    image: `${UP}2026/05/20230922_-_PDP_Asset_1_-_C-Tetra_-_Packshot_-_DE.avif`,
    skinTypes: ['Pigmentatie', 'Rijpere huid', 'Droge huid', 'Hormonale huid'],
    note: 'Lichte vitamine C voor elke dag, ook onder make-up.',
    why: 'Het serum dat de huid beschermt en direct laat stralen. Met 7% tetrahexyldecyl ascorbate wordt de huid beschermd tegen vrije radicalen, terwijl de collageenaanmaak wordt gestimuleerd en pigmentvlekken zichtbaar verminderen. Vitamine E versterkt de antioxidatieve werking, glycerine houdt de huid soepel. De lichte, snel intrekkende formule maakt C-Tetra ideaal voor dagelijks gebruik, juist ook onder make-up.',
    howTo: use.vitC,
    keyIngredients: ['7% tetrahexyldecyl ascorbate', 'Vitamine E', 'Glycerine'],
    rating: 0,
    reviews: 0,
    refill: true,
    pairs: ['medik8-daily-radiance-vitamine-c', 'medik8-hydr8-b5'],
  },
  {
    id: 'medik8-c-tetra-luxe',
    wooId: 10616,
    name: 'C-Tetra Luxe',
    brand: 'Medik8',
    category: 'Serum',
    step: 'Behandelen',
    price: 82,
    image: `${UP}2026/05/20250404_-_PDP_Asset_1_-_C-Tetra_Luxe_-_Packshot_-_DE.avif`,
    skinTypes: ['Pigmentatie', 'Rijpere huid', 'Droge huid', 'Hormonale huid'],
    note: 'Rijke vitamine C (14%) met een directe glow.',
    why: 'C-Tetra Luxe beschermt de huid intensief en laat haar zichtbaar stralen. Met 14% tetrahexyldecyl ascorbate biedt het krachtige antioxidatieve bescherming, stimuleert het de collageenaanmaak en helpt het pigmentvlekken verminderen. Squalane en maracuja-olie voeden en verzachten. De zijdezachte, lipiderijke textuur is ideaal als je een intensieve anti-ageing upgrade zoekt.',
    howTo: use.vitC,
    keyIngredients: ['14% tetrahexyldecyl ascorbate', 'Vitamine E', 'Squalane', 'Maracuja-olie'],
    rating: 0,
    reviews: 0,
    refill: true,
    pairs: ['medik8-daily-radiance-vitamine-c', 'medik8-hydr8-b5'],
  },
  {
    id: 'medik8-niacinamide-peptides',
    wooId: 10615,
    name: 'Niacinamide Peptides',
    brand: 'Medik8',
    category: 'Serum',
    step: 'Behandelen',
    price: 60,
    image: `${UP}2026/05/20250922-NiacinamidePeptides-PDP-Carousel-Asset1-ROW.avif`,
    skinTypes: ['Hormonale huid', 'Vette huid', 'Pigmentatie', 'Rijpere huid'],
    note: 'Verkleint poriën, remt roodheid en brengt talg in balans.',
    why: 'Niacinamide Peptides bevat een bewezen 10% niacinamide-complex dat poriën verkleint, roodheid vermindert en de huidbarrière versterkt. Crystalide-peptide zorgt voor een stralende, egale teint; N-acetyl glucosamine exfolieert zacht zonder zuur. Zinc PCA reguleert de talgproductie en voorkomt verstopping, hyaluronzuur hydrateert. Precies wat een huid met hormonale onzuiverheden nodig heeft.',
    howTo: use.serum,
    keyIngredients: ['10% niacinamide', 'Crystalide peptide', 'N-acetyl glucosamine', 'Zinc PCA'],
    rating: 0,
    reviews: 0,
    refill: true,
    pairs: ['clarifying-foam', 'balance-moisturizer-glycolic-acid-activator'],
  },
  {
    id: 'medik8-liquid-peptides-advanced-mp',
    wooId: 10607,
    name: 'Liquid Peptides Advanced MP',
    brand: 'Medik8',
    category: 'Serum',
    step: 'Behandelen',
    price: 99.95,
    image: `${UP}2026/05/20241013_-_PDP_Asset_1_-_Liquid_Peptides_Advanced_MP_-_Packshot_-_DE.avif`,
    skinTypes: ['Rijpere huid', 'Droge huid'],
    note: 'Dertien peptiden die expressielijntjes zichtbaar verzachten.',
    why: 'Liquid Peptides Advanced MP met dual miniproteins vermindert expressielijntjes al binnen tien minuten en helpt diepe rimpels zichtbaar te verminderen in acht weken. De formule bevat dertien anti-ageing peptiden, waaronder Matrixyl 3000, om de huid te liften en te verstevigen. Een prebiotisch complex ondersteunt de huidbarrière, microfiller-hyaluronzuur vult fijne lijntjes direct op. Als dat niet klinkt als een droom die uitkomt!',
    howTo: use.serum,
    keyIngredients: ['13 peptiden (o.a. Matrixyl 3000)', 'Dual miniproteins', 'Prebiotisch complex', 'Hyaluronzuur'],
    rating: 0,
    reviews: 0,
    refill: true,
    pairs: ['medik8-advanced-pro-collagen-peptide-cream', 'medik8-chrystal-retinal-1'],
  },
  {
    id: 'medik8-hydr8-b5',
    wooId: 10611,
    name: 'Hydr8 B5',
    brand: 'Medik8',
    category: 'Serum',
    step: 'Hydrateren',
    price: 58.25,
    image: `${UP}2026/05/20230922_-_PDP_Asset_1_-_Hydr8_B5_-_Packshot_-_DE.avif`,
    skinTypes: ['Droge huid', 'Gevoelige huid', 'Rijpere huid', 'Hormonale huid'],
    note: 'Licht serum dat vocht aanvult en roodheid verzacht.',
    why: 'Hydr8 B5 vult het vochtgehalte van je huid aan, herstelt het huidvolume en verzacht zichtbare roodheid. Een lichtgewicht serum voor ieder huidtype dat direct inwerkt op droogheid, fijne lijntjes en gevoeligheid. Het wordt ook gebruikt in professionele behandelingen zoals microneedling.',
    howTo: use.serum,
    keyIngredients: ['Hyaluronzuur', 'Vitamine B5'],
    rating: 0,
    reviews: 0,
    refill: true,
    pairs: ['medik8-c-tetra-serum', 'total-moisture-daily-facial-cream'],
  },
  {
    id: 'medik8-hydrate-b5-intense',
    wooId: 10608,
    name: 'Hydr8 B5 Intense',
    brand: 'Medik8',
    category: 'Serum',
    step: 'Hydrateren',
    price: 74.95,
    image: `${UP}2026/05/20230922_-_PDP_Asset_1_-_Hydr8_B5_Intense_-_Packshot_-_DE.avif`,
    skinTypes: ['Droge huid', 'Rijpere huid'],
    note: 'Intensieve hydratatie in meerdere huidlagen tegelijk.',
    why: 'Het serum dat de huid direct verfrist en langdurig hydrateert. Multi-weight hyaluronzuur trekt vocht aan en houdt het vast in meerdere huidlagen, vitamine B5 versterkt de barrière. Een NMF-complex verhoogt het vochtgehalte en de elasticiteit, saskatoonbes beschermt tegen vrije radicalen. Licht en snel intrekkend, ideaal voor elke dag.',
    howTo: use.serum,
    keyIngredients: ['Multi-weight hyaluronzuur', 'Vitamine B5', 'NMF-complex', 'Saskatoonbes'],
    rating: 0,
    reviews: 0,
    refill: true,
    pairs: ['total-moisture-daily-facial-cream', 'medik8-c-tetra-luxe'],
  },
  {
    id: 'medik8-advanced-pro-collagen-peptide-cream',
    wooId: 10637,
    name: 'Advanced Pro-Collagen + Peptide Cream',
    brand: 'Medik8',
    category: 'Crème',
    step: 'Hydrateren',
    price: 100,
    image: `${UP}2026/05/20250611_-_Advanced_Pro-Collagen_2B_-_PDP_-_Asset_1_-_Packshot_-_DE.avif`,
    skinTypes: ['Rijpere huid'],
    note: 'Verstevigt zichtbaar en verzacht rimpels. Het salonresultaat, thuis verlengd.',
    why: 'De crème die de huid zichtbaar verstevigt en rimpels vermindert. Growth Factor Miniprotein verhoogt het collageen-, elastine- en hyaluronzuurgehalte voor een stevigere, gladdere huid. Een NAD-longevity booster vertraagt zichtbare veroudering, carnosine beschermt tegen glycatie: het proces waarbij suikers collageen beschadigen. Klinisch onderzoek laat al na zeven dagen een betere elasticiteit zien.',
    howTo: use.cream,
    keyIngredients: ['Growth Factor Miniprotein', 'NAD-booster', 'Carnosine'],
    rating: 0,
    reviews: 0,
    refill: true,
    pairs: ['medik8-liquid-peptides-advanced-mp', 'medik8-chrystal-retinal-1'],
  },
  {
    id: 'total-moisture-daily-facial-cream',
    wooId: 10579,
    name: 'Total Moisture Daily Facial Cream',
    brand: 'Medik8',
    category: 'Crème',
    step: 'Hydrateren',
    price: 56,
    image: `${UP}2026/05/20231114_-_PDP_Asset_1_-_Total_Moisture_-_Packshot_-_DE.avif`,
    skinTypes: ['Droge huid', 'Rijpere huid', 'Hormonale huid'],
    note: 'Lichte dagcrème die de hele dag hydrateert. Met refill.',
    why: 'De crème die de huid de hele dag intensief hydrateert en beschermt. Multi-weight hyaluronzuur hydrateert diep, ceramiden versterken de barrière en gaan vochtverlies tegen. Squalane voedt, prebiotica ondersteunen de natuurlijke balans. Schaf hem één keer aan en koop daarna de refill: dat scheelt kosten en is duurzamer.',
    howTo: use.cream,
    keyIngredients: ['Multi-weight hyaluronzuur', 'Ceramiden', 'Squalane', 'Prebiotica'],
    rating: 0,
    reviews: 0,
    refill: true,
    pairs: ['medik8-hydr8-b5', 'medik8-c-tetra-serum'],
  },
  {
    id: 'balance-moisturizer-glycolic-acid-activator',
    wooId: 10600,
    name: 'Balance Moisturiser & Glycolic Acid Activator',
    brand: 'Medik8',
    category: 'Crème',
    step: 'Hydrateren',
    price: 64,
    image: `${UP}2026/05/20230922_-_PDP_Asset_1_-_Balance_Moisturiser_and_Glycolic_Acid_Activator_-_Packshot_-_DE.avif`,
    skinTypes: ['Hormonale huid', 'Vette huid', 'Pigmentatie'],
    note: 'Crème + activator voor een gecombineerde of onzuivere huid.',
    why: 'De crème die hydrateert en de poriën verfijnt. Niacinamide reguleert de talgproductie, vermindert roodheid en versterkt de barrière; prebiotica brengen het microbioom in balans. De glycolic acid activator is een zachte exfoliant die dode huidcellen verwijdert en de textuur gladder maakt. Samen een duo dat ik inzet bij een gecombineerde of onzuivere huid.',
    howTo: 'Meng één pompje crème met één à twee druppels activator. ’s Avonds na je serum; begin om de dag en bouw op.',
    keyIngredients: ['Niacinamide', 'Prebiotica', 'Hyaluronzuur', 'Glycolzuur'],
    rating: 0,
    reviews: 0,
    refill: true,
    pairs: ['clarifying-foam', 'medik8-niacinamide-peptides'],
  },
  {
    id: 'medik8-surface-radiance-cleanse',
    wooId: 10613,
    name: 'Surface Radiance Cleanse',
    brand: 'Medik8',
    category: 'Reiniging',
    step: 'Reinigen',
    price: 32,
    image: `${UP}2026/05/20230922_-_PDP_Asset_1_-_Surface_Radiance_Cleanse_-_Packshot_-_NL.avif`,
    skinTypes: ['Hormonale huid', 'Vette huid', 'Pigmentatie'],
    note: 'Gelreiniger die zacht exfolieert en direct verheldert.',
    why: 'Een gelreiniger die de huid zacht exfolieert en direct verheldert. Amandelzuur, melkzuur en salicylzuur verwijderen dode huidcellen en onzuiverheden en verfijnen de poriën. Glycerine hydrateert, mangosteenextract beschermt tegen vrije radicalen. Een ideale voorbereiding op de rest van je routine, en eenvoudig dagelijks te gebruiken.',
    howTo: use.cleanser,
    keyIngredients: ['Amandelzuur', 'Melkzuur', 'Salicylzuur', 'Mangosteen'],
    rating: 0,
    reviews: 0,
    refill: true,
    pairs: ['medik8-daily-refreshing-toner', 'medik8-c-tetra-serum'],
  },
  {
    id: 'clarifying-foam',
    wooId: 10598,
    name: 'Clarifying Foam',
    brand: 'Medik8',
    category: 'Reiniging',
    step: 'Reinigen',
    price: 32,
    image: `${UP}2026/05/20230922_-_PDP_Asset_1_-_Clarifying_Foam_-_Packshot_-_DE.avif`,
    skinTypes: ['Hormonale huid', 'Vette huid'],
    note: 'Reinigingsschuim voor een onzuivere, acnegevoelige huid.',
    why: 'Speciaal ontwikkeld voor de onzuivere en acnegevoelige huid. Salicylzuur en amandelzuur dringen diep in de poriën en verminderen verstoppingen. Niacinamide kalmeert en reguleert talg, tea tree houdt de huid fris. Het lichte schuim reinigt grondig zonder uit te drogen, dus je kunt het elke dag gebruiken.',
    howTo: use.cleanser,
    keyIngredients: ['Salicylzuur', 'Amandelzuur', 'Niacinamide', 'Tea tree'],
    rating: 0,
    reviews: 0,
    refill: true,
    pairs: ['medik8-niacinamide-peptides', 'balance-moisturizer-glycolic-acid-activator'],
  },
  {
    id: 'lipid-balance-cleansing-oil',
    wooId: 10604,
    name: 'Lipid Balance Cleansing Oil',
    brand: 'Medik8',
    category: 'Reiniging',
    step: 'Reinigen',
    price: 38,
    image: `${UP}2026/05/20230922_-_PDP_Asset_1_-_Lipid_Balance_Cleansing_Oil_-_Packshot_-_DE.avif`,
    skinTypes: ['Droge huid', 'Rijpere huid', 'Gevoelige huid'],
    note: 'Lost make-up en SPF op zonder je barrière te verstoren.',
    why: 'De reinigingsolie die make-up, SPF en onzuiverheden moeiteloos oplost zonder de huidbarrière te verstoren. Omega-6-rijke saffloerolie herstelt de lipidenbalans, vitamine E beschermt. Bij contact met water verandert de olie in een zijdezachte melk. De ideale eerste stap van dubbel reinigen.',
    howTo: 'Op een droge huid masseren, water toevoegen tot het melkt en afspoelen. Droeg je make-up of SPF, reinig dan nog een tweede keer.',
    keyIngredients: ['Saffloerolie (omega 6)', 'Vitamine E'],
    rating: 0,
    reviews: 0,
    refill: true,
    pairs: ['medik8-surface-radiance-cleanse', 'medik8-daily-refreshing-toner'],
  },
  {
    id: 'medik8-daily-refreshing-toner',
    wooId: 10717,
    name: 'Daily Refresh Balancing Toner',
    brand: 'Medik8',
    category: 'Toner',
    step: 'Reinigen',
    price: 32,
    image: `${UP}2026/05/20230922_-_PDP_Asset_1_-_Daily_Refresh_Balancing_Toner_-_Packshot_-_DE-1.avif`,
    skinTypes: ['Droge huid', 'Hormonale huid', 'Rijpere huid'],
    note: 'Balanceert na het reinigen, zodat je serum beter wordt opgenomen.',
    why: 'De Daily Refresh Toner met bloemenwater is een heerlijke verzorging. Hij balanceert de huid en voorkomt uitdroging door kraanwater. Alles wat je daarna aanbrengt — crème, serum, masker — wordt beter opgenomen, dus je hebt minder product nodig.',
    howTo: use.toner,
    keyIngredients: ['Bloemenwater'],
    rating: 0,
    reviews: 0,
    refill: true,
    pairs: ['medik8-surface-radiance-cleanse', 'medik8-c-tetra-serum'],
  },

  // ——— emerginC ———
  {
    id: 'emerginc-vitamine-c-serum',
    wooId: 1659,
    name: 'Vitamin C Serum 12%',
    brand: 'emerginC',
    category: 'Serum',
    step: 'Behandelen',
    price: 95,
    image: `${UP}2019/12/emerginc-vitamine-c-serum.jpeg`,
    skinTypes: ['Pigmentatie', 'Rijpere huid', 'Hormonale huid'],
    note: 'Stabiele vitamine C tegen pigment, zonschade en veroudering.',
    why: 'Een hoge concentratie van het stabiele stay-C 50, voor alle huidtypes. Helpt bij de aanmaak van collageen, gaat pigmentatie, zonschade en huidveroudering tegen en werkt ontstekingsremmend. Na je dertigste onmisbaar. Belangrijk bij je keuze is de juiste concentratie én dat de vitamine C niet geoxideerd is — geoxideerde vitamine C doet je huid meer kwaad dan goed.',
    howTo: use.vitC,
    keyIngredients: ['Stay-C 50 (12%)'],
    rating: 0,
    reviews: 0,
    refill: true,
    pairs: ['emerginc-sun-spf-50-tinted', 'emerginc-peach-toner'],
  },
  {
    id: 'vitamine-c-20',
    wooId: 6975,
    name: 'Vitamin C 20%',
    brand: 'emerginC',
    category: 'Serum',
    step: 'Behandelen',
    price: 103,
    image: `${UP}2019/12/emerginC-vitaminC-serum20HR.jpg`,
    skinTypes: ['Pigmentatie', 'Hormonale huid', 'Rijpere huid'],
    note: 'Stabiele vitamine C: pigment in drie maanden zichtbaar lichter.',
    why: 'Dit stabiele stay-C serum vermindert pigmentatie in drie maanden zichtbaar: het pigment wordt langzaam lichter. Het bevat ook hyaluronzuur voor de vochtbalans en werkt ontstekingsremmend bij acne. Vitamine C is een van de weinige bewezen anti-agers — vanaf je dertigste een must-have.',
    howTo: use.vitC,
    keyIngredients: ['Stay-C 20%', 'Hyaluronzuur'],
    rating: 0,
    reviews: 0,
    refill: true,
    pairs: ['emerginc-sun-spf-50-tinted', 'emerginc-kombucha-cleanser'],
  },
  {
    id: 'multi-vitamine-retinol-serum',
    wooId: 10011,
    name: 'Multi Vitamine & Retinol Serum',
    brand: 'emerginC',
    category: 'Serum',
    step: 'Behandelen',
    price: 95,
    image: `${UP}2025/07/IMG_7639.jpg`,
    skinTypes: ['Rijpere huid', 'Pigmentatie'],
    note: 'Retinolserum voor een heldere, zuivere huid.',
    why: 'Een serum met retinol en vitaminen dat zorgt voor een heldere, zuivere huid.',
    howTo: use.retinal,
    keyIngredients: ['Retinol', 'Multivitaminen'],
    rating: 0,
    reviews: 0,
    refill: true,
    pairs: ['emerginc-sun-spf-50-tinted', 'emerginc-phytocell-cream'],
  },
  {
    id: 'oogserum-roller-astaxantine',
    wooId: 1034,
    name: 'Eyelight Serum',
    brand: 'emerginC',
    category: 'Serum',
    step: 'Behandelen',
    price: 63,
    image: `${UP}2019/11/Emergin-c-Eyelight-Serum.jpg`,
    skinTypes: ['Rijpere huid', 'Droge huid'],
    note: 'Roll-on oogserum tegen donkere kringen en lijntjes.',
    why: 'Dit roll-on serum is sterk tegen donkere kringen én anti-ageing. Geen ander product bevat zoveel actieve ingrediënten, waaronder de super-antioxidant astaxanthine. Druivenstamcellen, kombucha en marine-extracten stimuleren de collageenaanmaak, aangevuld met groene en witte thee, yerba mate, pijnboomschors en hyaluronzuur.',
    howTo: use.eye,
    keyIngredients: ['Astaxanthine', 'Druivenstamcellen', 'Kombucha', 'Hyaluronzuur'],
    rating: 0,
    reviews: 0,
    refill: true,
    pairs: ['eyelight-cream', 'emerginc-vitamine-c-serum'],
  },
  {
    id: '40-hydra-repair-capsules',
    wooId: 1087,
    name: 'Hydra Repair Capsules',
    brand: 'emerginC',
    category: 'Serum',
    step: 'Hydrateren',
    price: 99,
    image: `${UP}2019/11/Hydra-Repair.jpg`,
    skinTypes: ['Gevoelige huid', 'Droge huid', 'Rijpere huid'],
    note: 'SOS-capsules voor een huid in crisis, ook bij rosacea.',
    why: 'Veertig hydraterende capsules op basis van avocado-olie, in te zetten als SOS-hulpmiddel bij elke huidcrisis. Ceramiden en antioxidanten helpen herstellen na een laserbehandeling of zonschade. De verfijnde avocado-olie wordt snel opgenomen, voedt en beschermt zodat de huid haar balans terugvindt. Bijzonder geschikt voor de gevoelige, vochtarme of rosacea-huid.',
    howTo: 'Ochtend en/of avond de inhoud van één capsule als serum aanbrengen. Mooi te combineren met de Earth Hydrating of Hyper-Vitalizer crème.',
    keyIngredients: ['Avocado-olie', 'Ceramiden', 'Antioxidanten'],
    rating: 0,
    reviews: 0,
    refill: true,
    pairs: ['emerginc-hyper-vitalizer-cream', 'emerginc-earth-hydrating-phytelene-cream'],
  },
  {
    id: 'emerginc-hyper-vitalizer-cream',
    wooId: 1665,
    name: 'Hyper-Vitalizer Cream',
    brand: 'emerginC',
    category: 'Crème',
    step: 'Hydrateren',
    price: 92,
    image: `${UP}2023/12/emerginC-hyper-vitalizer-cream.png`,
    skinTypes: ['Gevoelige huid', 'Droge huid', 'Rijpere huid'],
    note: 'Rijke crème, uitstekend voor een rode, gevoelige huid.',
    why: 'Deze rijke, voedende crème bevat alfa-liponzuur — een “meester-antioxidant” die krachtig ontstekingsremmend en celactiverend is — plus co-enzym Q10 en luteïne, een plantaardige antioxidant die helpt beschermen tegen UVB-schade. Geschikt voor alle huidtypes en uitstekend voor een rode, gevoelige huid.',
    howTo: use.cream,
    keyIngredients: ['Alfa-liponzuur', 'Co-enzym Q10', 'Luteïne'],
    rating: 0,
    reviews: 0,
    refill: true,
    pairs: ['40-hydra-repair-capsules', 'emerginc-lavender-toner'],
  },
  {
    id: 'emerginc-phytocell-cream',
    wooId: 9184,
    name: 'Phytocell Cream',
    brand: 'emerginC',
    category: 'Crème',
    step: 'Hydrateren',
    price: 95,
    image: `${UP}2023/11/emerginc-phytocell-cream.png`,
    skinTypes: ['Rijpere huid', 'Droge huid'],
    note: 'Intens hydraterend en age-fighting, met druivenstamcellen.',
    why: 'Een intensief hydraterende, age-fighting crème met geconcentreerde druivenstamcellen. Ontworpen om de huid te beschermen, te revitaliseren en te vernieuwen, en bestrijdt schade door vrije radicalen.',
    howTo: use.cream,
    keyIngredients: ['Druivenstamcellen'],
    rating: 0,
    reviews: 0,
    refill: true,
    pairs: ['emerginc-vitamine-c-serum', 'eyelight-cream'],
  },
  {
    id: 'emerginc-earth-hydrating-phytelene-cream',
    wooId: 1084,
    name: 'Earth Hydrating Phytelene Cream',
    brand: 'emerginC',
    category: 'Crème',
    step: 'Hydrateren',
    price: 92,
    image: `${UP}2019/11/emerginC-earth-hydrating-phytelene-cream.png`,
    skinTypes: ['Gevoelige huid', 'Rijpere huid', 'Hormonale huid'],
    note: 'Lichte dag- en nachtcrème voor een egale teint zonder roodheid.',
    why: 'Deze crème bevat chronocycline, dat de aanmaak van vitamine D bevordert. Heerlijk hydraterend, licht, en zorgt voor een egale teint zonder roodheid. Zowel als dag- als nachtverzorging te gebruiken.',
    howTo: use.cream,
    keyIngredients: ['Chronocycline'],
    rating: 0,
    reviews: 0,
    refill: true,
    pairs: ['40-hydra-repair-capsules', 'emerginc-lavender-toner'],
  },
  {
    id: 'emerginc-active-hydrating-complex',
    wooId: 1085,
    name: 'Active Hydrating Complex',
    brand: 'emerginC',
    category: 'Crème',
    step: 'Hydrateren',
    price: 64,
    image: `${UP}2019/11/emerginC-active-hydrating-complex.png`,
    skinTypes: ['Vette huid', 'Gevoelige huid', 'Hormonale huid'],
    note: 'Lichte, olievrije gelcrème die kalmeert en hydrateert.',
    why: 'Een lichte, olievrije gelcrème die de huid verzacht en hydrateert. Geschikt voor alle huidtypes en uitstekend voor een vette, gevoelige of vochtarme huid. Helpt bij droogheid, schilfering, roodheid en na zon of wind.',
    howTo: use.cream,
    keyIngredients: ['Olievrije gelformule'],
    rating: 0,
    reviews: 0,
    refill: true,
    pairs: ['emerginc-lavender-toner', 'emerginc-pomegranate-gel-cleanser'],
  },
  {
    id: 'emerginc-complexion-control',
    wooId: 7393,
    name: 'Complexion Control',
    brand: 'emerginC',
    category: 'Crème',
    step: 'Hydrateren',
    price: 61,
    image: `${UP}2021/01/emerginC-complexion-control.png`,
    skinTypes: ['Vette huid', 'Hormonale huid'],
    note: 'Lichte crème die talg reguleert bij een onzuivere huid.',
    why: 'Een lichte, hydraterende crème die overtollige talg reguleert en onzuiverheden onder controle houdt. Voor een vette, gecombineerde of onzuivere huid: helpt bij verstopte poriën en brengt de huid in balans zonder uit te drogen.',
    howTo: use.cream,
    keyIngredients: ['Talgregulerend complex'],
    rating: 0,
    reviews: 0,
    refill: true,
    pairs: ['emerginc-pomegranate-gel-cleanser', 'spirulina-toner'],
  },
  {
    id: 'eyelight-cream',
    wooId: 1083,
    name: 'Eyelight Cream',
    brand: 'emerginC',
    category: 'Crème',
    step: 'Hydrateren',
    price: 75,
    image: `${UP}2019/11/SO-eyelight-cream.jpg`,
    skinTypes: ['Rijpere huid', 'Pigmentatie'],
    note: 'Rijke oogcrème tegen lijntjes en donkere kringen.',
    why: 'Deze intens rijke oogcrème bevat astaxanthine, een super-antioxidant die sterk is tegen lijntjes en donkere kringen. Geeft de huid rond je ogen een stralende glans.',
    howTo: use.eye,
    keyIngredients: ['Astaxanthine'],
    rating: 0,
    reviews: 0,
    refill: true,
    pairs: ['oogserum-roller-astaxantine', 'emerginc-phytocell-cream'],
  },
  {
    id: 'rawceuticals-eye-and-lipcraft',
    wooId: 933,
    name: 'Rawceuticals Eye & Lip Craft',
    brand: 'emerginC',
    category: 'Crème',
    step: 'Hydrateren',
    price: 89,
    image: `${UP}2019/11/EyeLip-Rawceutical.jpg`,
    skinTypes: ['Rijpere huid', 'Droge huid'],
    note: 'Rijke, koudgeperste verzorging voor ogen en lippen.',
    why: 'Een zeer rijke, voedende “cold pressed” anti-ageing moisturizer — nooit hoger verhit dan 47 graden — met cacaoboter, hennepzaadolie en wortelzaadolie. Natuurlijk caroteen en vitamine A; de omegazuren uit hennepzaad verbeteren de elasticiteit.',
    howTo: use.eye,
    keyIngredients: ['Cacaoboter', 'Hennepzaadolie', 'Wortelzaadolie'],
    rating: 0,
    reviews: 0,
    refill: true,
    pairs: ['rawceuticals-facial-oil', 'rawceuticals-blend'],
  },
  {
    id: 'rawceuticals-blend',
    wooId: 6969,
    name: 'Rawceuticals Blend',
    brand: 'emerginC',
    category: 'Crème',
    step: 'Hydrateren',
    price: 129,
    image: `${UP}2020/09/Rawceuticals-Blend.png`,
    skinTypes: ['Rijpere huid', 'Droge huid', 'Hormonale huid'],
    note: 'Rijke, koudgeperste crème voor een egale teint en textuur.',
    why: 'Een rijke, voedende, actieve “geperste” moisturizer die beschermt tegen omgevingsinvloeden en fijne lijntjes en rimpels bestrijdt. Je krijgt er een egale teint en textuur van.',
    howTo: use.cream,
    keyIngredients: ['Koudgeperste plantenoliën'],
    rating: 0,
    reviews: 0,
    refill: true,
    pairs: ['cleansing-olie-rawceuticals', 'raw-boreal-tonic'],
  },
  {
    id: 'rawceuticals-facial-oil',
    wooId: 9600,
    name: 'Rawceuticals Facial Oil',
    brand: 'emerginC',
    category: 'Olie',
    step: 'Hydrateren',
    price: 105,
    image: `${UP}2024/12/Rawceuticals-Facial-Oil-Beauty-Bewust.jpg`,
    skinTypes: ['Rijpere huid', 'Droge huid', 'Gevoelige huid', 'Hormonale huid'],
    note: 'Koudgeperste oliën tegen droge lijntjes, roodheid en rosacea.',
    why: 'Een effectieve gezichtsolie met koudgeperste oliën van hennep, pompoenzaad, druivenpit, marula, jojoba, olijf en cederhout. Droge lijntjes vervagen, er verschijnt een gezonde gloed. Krachtig anti-ageing, en vermindert roodheid en ontstekingsreacties zoals bij rosacea en couperose.',
    howTo: '’s Avonds enkele druppels warm wrijven tussen je handen en in de huid drukken, als laatste stap of door je crème.',
    keyIngredients: ['Hennepzaadolie', 'Pompoenzaadolie', 'Marula', 'Jojoba'],
    rating: 0,
    reviews: 0,
    refill: true,
    pairs: ['cleansing-olie-rawceuticals', 'raw-boreal-tonic'],
  },
  {
    id: 'emerginc-sun-spf-50-tinted',
    wooId: 10550,
    name: 'Sun SPF 50+ Tinted',
    brand: 'emerginC',
    category: 'SPF',
    step: 'Beschermen',
    price: 45,
    image: `${UP}2026/04/emerginc-spf-50-tinted-fair.jpg`,
    skinTypes: ['Pigmentatie', 'Hormonale huid', 'Gevoelige huid'],
    note: 'Minerale, licht getinte bescherming voor elke dag.',
    why: 'Een minerale zonnebrandcrème met zinkoxide en SPF 50+, licht getint en geschikt voor dagelijks gebruik. Niet vettig, met aloë vera en plantaardige glycerine voor hydratatie. Plantaardige rutine en antioxidanten beschermen tegen omgevingsinvloeden, ook blauw licht. De huid oogt egaal en voelt comfortabel.',
    howTo: use.spf,
    keyIngredients: ['Zinkoxide', 'Aloë vera', 'Plantaardige glycerine', 'Rutine'],
    rating: 0,
    reviews: 0,
    refill: true,
    pairs: ['emerginc-vitamine-c-serum', 'emerginc-kombucha-cleanser'],
  },
  {
    id: 'emerginc-vitamin-c-retinol-mask',
    wooId: 6973,
    name: 'Vitamin C + Retinol Mask',
    brand: 'emerginC',
    category: 'Masker',
    step: 'Behandelen',
    price: 62,
    image: `${UP}2020/09/emerginC-vitaminc-retinol-mask.png`,
    skinTypes: ['Pigmentatie', 'Hormonale huid', 'Rijpere huid'],
    note: 'Kleimasker dat na één keer zichtbaar verheldert.',
    why: 'Een krachtig kleimasker dat al na één keer zichtbaar resultaat geeft: het verwijdert dode huidcellen, egaliseert de teint en geeft een natuurlijke glans. Voor alle huidtypes behalve de zeer gevoelige huid; helpt bij zonschade, onzuiverheden, pigment en fijne lijntjes.',
    howTo: `${use.mask} Niet op een zeer gevoelige of geïrriteerde huid.`,
    keyIngredients: ['Vitamine C', 'Retinol', 'Klei'],
    rating: 0,
    reviews: 0,
    refill: false,
    pairs: ['vitamine-c-20', 'emerginc-lavender-toner'],
  },
  {
    id: 'vitality-mask',
    wooId: 6971,
    name: 'Vitality Mask',
    brand: 'emerginC',
    category: 'Masker',
    step: 'Hydrateren',
    price: 62,
    image: `${UP}2020/09/emerginC-vitality-mask.png`,
    skinTypes: ['Droge huid', 'Rijpere huid'],
    note: 'Hydraterend (nacht)masker met komkommer.',
    why: 'Voor een droge of vochtarme huid. Dit masker kun je ook ’s nachts laten zitten: het herstelt je huid terwijl je slaapt. Komkommerextract zorgt voor een zachte, stralende en goed gehydrateerde huid zonder vet gevoel. Vooral in de herfst heeft je huid die extra aandacht nodig.',
    howTo: 'Eén tot twee keer per week een dunne laag, tien minuten of als nachtmasker laten zitten.',
    keyIngredients: ['Komkommerextract'],
    rating: 0,
    reviews: 0,
    refill: false,
    pairs: ['emerginc-earth-hydrating-phytelene-cream', '40-hydra-repair-capsules'],
  },
  {
    id: 'emerginc-kombucha-cleanser',
    wooId: 810,
    name: 'Kombucha Cleanser',
    brand: 'emerginC',
    category: 'Reiniging',
    step: 'Reinigen',
    price: 45,
    image: `${UP}2019/11/Kombucha-Cleanser.jpg`,
    skinTypes: ['Gevoelige huid', 'Pigmentatie', 'Hormonale huid', 'Droge huid'],
    note: 'Zijdezachte reiniger die gevoeligheid kalmeert.',
    why: 'Deze cleanser ruikt heerlijk naar citrus en is zijdezacht. Aloë vera, algenextract en groene thee houden de huid in balans en kalmeren gevoeligheid. Verbetert de vochtbalans, helpt bij pigmentatie en bevordert de collageenaanmaak. Volg hem op met de Spirulina Toner.',
    howTo: use.cleanser,
    keyIngredients: ['Kombucha', 'Aloë vera', 'Algenextract', 'Groene thee'],
    rating: 0,
    reviews: 0,
    refill: true,
    pairs: ['spirulina-toner', 'emerginc-vitamine-c-serum'],
  },
  {
    id: 'emerginc-aromatic-cream-tea-cleanser',
    wooId: 935,
    name: 'Aromatic Cream Tea Cleanser',
    brand: 'emerginC',
    category: 'Reiniging',
    step: 'Reinigen',
    price: 48,
    image: `${UP}2019/11/emerginC-aromatic-cream-tea-cleanser.png`,
    skinTypes: ['Gevoelige huid', 'Pigmentatie', 'Rijpere huid'],
    note: 'Zachte, romige reiniger die roodheid kalmeert.',
    why: 'Een zachte, romige reiniger met groene, rode en witte thee die vuil, make-up en overtollige olie verwijdert. Voor alle huidtypes; helpt bij een doffe huid, pigment, af en toe een onzuiverheid en fijne lijntjes. Reinigt zacht en kalmeert roodheid.',
    howTo: use.cleanser,
    keyIngredients: ['Groene thee', 'Rode thee', 'Witte thee'],
    rating: 0,
    reviews: 0,
    refill: true,
    pairs: ['emerginc-lavender-toner', 'emerginc-vitamine-c-serum'],
  },
  {
    id: 'emerginc-pomegranate-gel-cleanser',
    wooId: 937,
    name: 'Pomegranate Gel Cleanser',
    brand: 'emerginC',
    category: 'Reiniging',
    step: 'Reinigen',
    price: 48,
    image: `${UP}2019/11/emerginC-pomegranate-gel-cleanser.png`,
    skinTypes: ['Vette huid', 'Hormonale huid', 'Pigmentatie'],
    note: 'Reinigt grondig en verwijdert talg zonder uit te drogen.',
    why: 'Een doeltreffende reinigingsgel met granaatappel en planten- en kruidenextracten. Reinigt diep, verwijdert make-up en overtollige olie, zonder uit te drogen. Geschikt voor alle huidtypes.',
    howTo: use.cleanser,
    keyIngredients: ['Granaatappel', 'Plantenextracten'],
    rating: 0,
    reviews: 0,
    refill: true,
    pairs: ['spirulina-toner', 'emerginc-complexion-control'],
  },
  {
    id: 'cleansing-olie-rawceuticals',
    wooId: 1088,
    name: 'Rawceuticals CLEAN Cleanser',
    brand: 'emerginC',
    category: 'Reiniging',
    step: 'Reinigen',
    price: 57,
    image: `${UP}2019/11/cleansing-olie-rawceuticals-clean.jpeg`,
    skinTypes: ['Droge huid', 'Rijpere huid', 'Gevoelige huid'],
    note: 'Waterloze reinigingsolie die make-up verwijdert en beschermt.',
    why: 'Een waterloze, zuiverende reinigingsolie die make-up effectief verwijdert en de vochtbalans helpt herstellen. Legt direct een beschermend laagje over de huid. Heerlijk naar sinaasappel geurend, met hennepzaad- en zonnebloemolie tegen schadelijke omgevingsinvloeden. Voor alle huidtypes, ’s ochtends en/of ’s avonds.',
    howTo: 'Op een droge huid masseren, daarna afnemen met een warm, vochtig doekje.',
    keyIngredients: ['Hennepzaadolie', 'Zonnebloemolie'],
    rating: 0,
    reviews: 0,
    refill: true,
    pairs: ['raw-boreal-tonic', 'rawceuticals-facial-oil'],
  },
  {
    id: 'blue-poppy-polish',
    wooId: 1058,
    name: 'Blue Poppy Enzyme Polish',
    brand: 'emerginC',
    category: 'Reiniging',
    step: 'Reinigen',
    price: 52,
    image: `${UP}2019/11/SO-blue-poppy-polish.jpg`,
    skinTypes: ['Hormonale huid', 'Gevoelige huid', 'Vette huid'],
    note: 'Enzympeeling die roodheid direct laat afnemen.',
    why: 'Deze polish geeft een dauwachtige hydratatieboost dankzij het enzym van de Chinese paddenstoel mu-err, waardoor roodheid direct afneemt. Kurkuma en tea tree zuiveren de huid.',
    howTo: 'Eén à twee keer per week na het reinigen, zacht masseren en afspoelen. Zo verwijder je dode huidcellen en wordt je huid sterker en weerbaarder.',
    keyIngredients: ['Mu-err-enzym', 'Kurkuma', 'Tea tree'],
    rating: 0,
    reviews: 0,
    refill: false,
    pairs: ['emerginc-kombucha-cleanser', 'spirulina-toner'],
  },
  {
    id: 'emerginc-peach-toner',
    wooId: 10716,
    name: 'Peach Toner',
    brand: 'emerginC',
    category: 'Toner',
    step: 'Reinigen',
    price: 39,
    image: `${UP}2023/12/emerginC-peach-toner.png`,
    skinTypes: ['Droge huid', 'Rijpere huid', 'Pigmentatie'],
    note: 'De favoriet van iedereen: hydrateert en ruikt heerlijk.',
    why: 'Deze peach toner is de favoriet van iedereen. De heerlijke geur, een goed gehydrateerde huid, en crème, serum en masker worden daarna beter opgenomen. Onmisbaar in je routine.',
    howTo: use.toner,
    keyIngredients: ['Perzikextract'],
    rating: 0,
    reviews: 0,
    refill: true,
    pairs: ['emerginc-vitamine-c-serum', 'emerginc-kombucha-cleanser'],
    bestseller: true,
  },
  {
    id: 'spirulina-toner',
    wooId: 1092,
    name: 'Spirulina Toner',
    brand: 'emerginC',
    category: 'Toner',
    step: 'Reinigen',
    price: 38,
    image: `${UP}2020/07/SO-spirulina-toner.jpg`,
    skinTypes: ['Droge huid', 'Pigmentatie', 'Gevoelige huid'],
    note: 'De meest hydraterende toner van emerginC.',
    why: 'Breng je huid in balans met de meest hydraterende toner van emerginC, op basis van druivenstamcellen en algenextract. Kombucha en whitonyl helpen tegen pigmentatie. Winnaar beste toner in de VS. Gebruik hem na de Kombucha Cleanser.',
    howTo: use.toner,
    keyIngredients: ['Druivenstamcellen', 'Algenextract', 'Kombucha', 'Whitonyl'],
    rating: 0,
    reviews: 0,
    refill: true,
    pairs: ['emerginc-kombucha-cleanser', 'emerginc-vitamine-c-serum'],
  },
  {
    id: 'emerginc-lavender-toner',
    wooId: 938,
    name: 'Lavender Toner',
    brand: 'emerginC',
    category: 'Toner',
    step: 'Reinigen',
    price: 39,
    image: `${UP}2019/11/emerginC-lavender-toner.png`,
    skinTypes: ['Gevoelige huid', 'Hormonale huid'],
    note: 'Milde, kalmerende toner voor een gevoelige huid.',
    why: 'Een milde, kalmerende toner met rustgevende plantenextracten. Voor alle huidtypes en bijzonder geschikt voor de gevoelige huid: verstevigt, balanceert de pH, kalmeert en verzacht roodheid.',
    howTo: use.toner,
    keyIngredients: ['Lavendel', 'Rustgevende plantenextracten'],
    rating: 0,
    reviews: 0,
    refill: true,
    pairs: ['emerginc-aromatic-cream-tea-cleanser', 'emerginc-hyper-vitalizer-cream'],
  },
  {
    id: 'raw-boreal-tonic',
    wooId: 890,
    name: 'Rawceuticals Boreal Tonic',
    brand: 'emerginC',
    category: 'Toner',
    step: 'Reinigen',
    price: 55,
    image: `${UP}2019/11/Boreal-tonic.jpg`,
    skinTypes: ['Droge huid', 'Rijpere huid', 'Gevoelige huid'],
    note: 'Remineraliserende toner die kalmeert en voedt.',
    why: 'De Boreal Tonic voelt als een mini-spabehandeling. Een unieke remineraliserende toner die de huid na het reinigen kalmeert, verzacht en voedt. Verbetert de textuur en voelt fris aan. Voor alle huidtypes, bijvoorbeeld na de Rawceuticals CLEAN.',
    howTo: use.toner,
    keyIngredients: ['Mineralen', 'Boreale plantenextracten'],
    rating: 0,
    reviews: 0,
    refill: true,
    pairs: ['cleansing-olie-rawceuticals', 'rawceuticals-facial-oil'],
  },
]
